import EditIcon from '@mui/icons-material/Edit';
import { Button, CircularProgress, Fade } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Challenge,
  GeneratedPlan,
  generatedContentApi,
} from 'src/api/generatedContent';
import ChallengeEditDialog from './ChallengeEditDialog';
import styles from './CirclePlanIdeas.module.scss';

interface CirclePlanChallengesProps {
  circlePlan: GeneratedPlan;
  selectedChallengeIndex: number;
  onSelectChallenge: (index: number) => void;
  onDevelopChallenge: (challenge: Challenge, planId: string) => void;
  onPlanUpdated?: (updatedPlan: GeneratedPlan) => void;
}

const CirclePlanChallenges: React.FC<CirclePlanChallengesProps> = ({
  circlePlan,
  selectedChallengeIndex,
  onSelectChallenge,
  onDevelopChallenge,
  onPlanUpdated,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonContainerRef = useRef<HTMLDivElement>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [challengeToEdit, setChallengeToEdit] = useState<Challenge | null>(
    null
  );
  const [challengeIndexToEdit, setChallengeIndexToEdit] = useState(-1);
  const [localCirclePlan, setLocalCirclePlan] =
    useState<GeneratedPlan>(circlePlan);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [buttonContainerStyle, setButtonContainerStyle] = useState({});

  // Update local plan when the prop changes
  useEffect(() => {
    setLocalCirclePlan(circlePlan);
  }, [circlePlan]);

  // Update the button container width based on the ideasSelection width
  useEffect(() => {
    if (selectedChallengeIndex !== -1 && containerRef.current) {
      const updateButtonWidth = () => {
        const containerWidth = containerRef.current?.offsetWidth || 0;
        const containerRect = containerRef.current?.getBoundingClientRect();

        if (containerWidth && containerRect) {
          setButtonContainerStyle({
            width: `${containerWidth}px`,
            left: `${containerRect.left}px`,
            right: 'auto',
            transform: 'none',
          });
        }
      };

      updateButtonWidth();
      window.addEventListener('resize', updateButtonWidth);

      return () => {
        window.removeEventListener('resize', updateButtonWidth);
      };
    }
  }, [selectedChallengeIndex]);

  const hasValidData =
    localCirclePlan &&
    Array.isArray(localCirclePlan.challenges) &&
    localCirclePlan.challenges.length > 0;

  const handleEditChallenge = (
    challenge: Challenge,
    index: number,
    event: React.MouseEvent
  ) => {
    event.stopPropagation();
    setChallengeToEdit(challenge);
    setChallengeIndexToEdit(index);
    setEditDialogOpen(true);
  };

  const handleChallengeUpdated = async () => {
    try {
      const updatedPlan = await generatedContentApi.getPlanById(
        localCirclePlan._id
      );
      if (updatedPlan) {
        setLocalCirclePlan(updatedPlan);
      }
    } catch (error) {
      console.error('Error fetching updated plan:', error);
    }
  };

  const handleDevelopSelectedChallenge = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (
      selectedChallengeIndex !== -1 &&
      localCirclePlan &&
      localCirclePlan.challenges &&
      localCirclePlan.challenges[selectedChallengeIndex]
    ) {
      const selectedChallenge =
        localCirclePlan.challenges[selectedChallengeIndex];

      onDevelopChallenge(selectedChallenge, localCirclePlan._id);
    } else {
      console.error('Invalid challenge selection:', {
        selectedChallengeIndex,
        hasChallenges: localCirclePlan?.challenges?.length > 0,
      });
    }
  };

  const handleLoadMoreChallenges = async () => {
    if (!localCirclePlan || !localCirclePlan._id) {
      return;
    }

    try {
      setIsLoadingMore(true);
      const currentCount = localCirclePlan.challenges.length;
      const loadedCount = localCirclePlan.metadata?.loadedChallengesCount || 0;
      const totalCount = localCirclePlan.metadata?.challengeCount || 0;

      if (loadedCount >= totalCount && totalCount > 0) {
        toast.success(`All ${loadedCount} challenges have been created`);
        setIsLoadingMore(false);
        return;
      }

      const responseId = localCirclePlan.metadata?.responseId;
      if (!responseId) {
        toast.error(
          `Missing response ID. Please try to regenerate the circle plan.`
        );
        setIsLoadingMore(false);
        return;
      }

      const newPlan = await generatedContentApi.generateMoreChallenges({
        planId: localCirclePlan._id,
        previous_response_id: responseId,
      });
      setLocalCirclePlan(newPlan);

      if (onPlanUpdated) {
        onPlanUpdated(newPlan);
      }

      toast.success(
        `Loaded ${newPlan.challenges.length - currentCount} more challenges`
      );
    } catch (error) {
      console.error('Error loading more challenges:', error);
      toast.error(
        typeof error === 'string'
          ? error
          : 'Failed to load more challenges. Please try again.'
      );
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Close develop button when clicking outside the cards
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const cards = document.querySelectorAll(`.${styles.ideaCard}`);
      let clickedOutside = true;

      // Check if we clicked on the floating button
      const floatingButton = document.querySelector(
        `.${styles.floatingButtonContainer}`
      );
      if (floatingButton && floatingButton.contains(event.target as Node)) {
        // Don't deselect if clicking on the floating button
        return;
      }

      cards.forEach((card) => {
        if (card.contains(event.target as Node)) {
          clickedOutside = false;
        }
      });

      if (clickedOutside && selectedChallengeIndex !== -1) {
        onSelectChallenge(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedChallengeIndex, onSelectChallenge]);

  const hasMoreChallenges = (() => {
    if (!localCirclePlan || !localCirclePlan.metadata) {
      return false;
    }

    const totalCount = localCirclePlan.metadata.challengeCount || 0;
    const loadedCount = localCirclePlan.metadata.loadedChallengesCount || 0;

    return totalCount > 0 && loadedCount < totalCount;
  })();

  return (
    <div className={styles.ideasSelection} ref={containerRef}>
      <div className={styles.planTitle}>
        <h1>{localCirclePlan.title || 'Circle Content Plan'}</h1>
      </div>

      <div className={styles.circlePlanHeader}>
        {localCirclePlan.instruction && (
          <div>
            <h2>Instruction:</h2>
            <p>{localCirclePlan.instruction}</p>
          </div>
        )}

        {localCirclePlan.keyTopics && localCirclePlan.keyTopics.length > 0 && (
          <div className={styles.keyThemes}>
            <h2>Key Topics:</h2>
            <ul>
              {localCirclePlan.keyTopics.map((topic, index) => (
                <li key={index}>
                  <strong>{topic.topic}</strong>
                  <ul>
                    <li>
                      <strong>Focus:</strong> {topic.focus}
                    </li>
                    <li>
                      <strong>Goal:</strong> {topic.goal}
                    </li>
                  </ul>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {hasValidData ? (
        <>
          <h3 className={styles.selectInstruction}>
            Select a challenge to develop into a full script:
          </h3>
          <div className={styles.ideasGrid}>
            {localCirclePlan.challenges.map((challenge, index) => (
              <div
                key={challenge._id || index}
                className={`${styles.ideaCard} ${
                  selectedChallengeIndex === index ? styles.selectedIdea : ''
                }`}
                onClick={() => onSelectChallenge(index)}
              >
                <div className={styles.ideaCardContent}>
                  <div className={styles.ideaCardHeader}>
                    <h4>{challenge.title}</h4>
                  </div>
                  <ul>
                    <li>
                      <strong>Challenge:</strong> {challenge.challenge}
                    </li>
                    <li>
                      <strong>Response:</strong> {challenge.response}
                    </li>
                  </ul>
                </div>
                <div className={styles.ideaCardFooter}>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={(e) => handleEditChallenge(challenge, index, e)}
                    variant="outlined"
                    color="primary"
                    className={styles.editButton}
                    sx={{ textTransform: 'none' }}
                  >
                    Edit
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {hasMoreChallenges && (
            <div className={styles.loadMoreContainer}>
              <button
                onClick={() => {
                  // console.log('Show More button clicked');
                  handleLoadMoreChallenges();
                }}
                disabled={isLoadingMore}
                className={styles.loadMoreButton}
              >
                {isLoadingMore ? (
                  <>
                    <CircularProgress
                      size={20}
                      color="inherit"
                      style={{ marginRight: '10px' }}
                    />
                    Loading More Challenges...
                  </>
                ) : (
                  'Generate More Challenges'
                )}
              </button>
              <div className={styles.challengeCount}>
                {hasMoreChallenges
                  ? `Showing ${localCirclePlan.challenges.length} of ${localCirclePlan.metadata?.challengeCount || 0} challenges`
                  : `All ${localCirclePlan.challenges.length} challenges generated`}
              </div>
            </div>
          )}
          {!hasMoreChallenges && localCirclePlan.challenges.length > 0 && (
            <div className={styles.loadMoreContainer}>
              <div className={styles.challengeCount}>
                All {localCirclePlan.challenges.length} challenges generated
              </div>
            </div>
          )}
        </>
      ) : (
        <div className={styles.noIdeas}>
          <p>No challenge found. Please try generating a new circle plan.</p>
        </div>
      )}

      {selectedChallengeIndex !== -1 && (
        <Fade in={selectedChallengeIndex !== -1} timeout={300}>
          <div
            className={styles.floatingButtonContainer}
            ref={buttonContainerRef}
            style={buttonContainerStyle}
          >
            <div
              className={styles.floatingButtonBackground}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={handleDevelopSelectedChallenge}
                className={styles.developButton}
              >
                Develop This Challenge
              </button>
            </div>
          </div>
        </Fade>
      )}

      <ChallengeEditDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        plan={localCirclePlan}
        challenge={challengeToEdit}
        challengeIndex={challengeIndexToEdit}
        onChallengeUpdated={handleChallengeUpdated}
      />
    </div>
  );
};

export default CirclePlanChallenges;
